import { Directory, File, Paths } from 'expo-file-system';
import { Platform } from 'react-native';

/**
 * Copy a picked file (from expo-document-picker or expo-image-picker) into
 * the app's persistent document directory so the URI survives across app
 * launches. On web, expo-file-system has limited write support — we return
 * the original URI (a blob: or data: URL) which lives for the session;
 * persistent web storage is a TODO that comes with cloud sync.
 *
 * The returned URI is suitable for storing in SQLite and feeding back to
 * <Image source={{ uri }}/> or an external opener.
 */
/** Web: files bigger than this stay session-only (a data-URL row this
 *  large would bloat the local DB and every list query that touches it). */
const WEB_INLINE_MAX_BYTES = 8 * 1024 * 1024;

export async function persistPickedFile(
  uri: string,
  fileName: string
): Promise<string> {
  if (Platform.OS === 'web') {
    // Wave 54: blob: URLs die on page reload — files LOOKED saved but
    // their bytes evaporated on refresh (user hit this). Inline the
    // bytes as a data: URL instead; every consumer (Image, pdf.js,
    // iframe preview, download anchor) accepts data URIs, and the row
    // survives reloads inside the local database. Real object storage
    // arrives with cloud sync.
    if (uri.startsWith('data:')) return uri;
    try {
      // eslint-disable-next-line no-undef
      const blob = await (await fetch(uri)).blob();
      if (blob.size > WEB_INLINE_MAX_BYTES) return uri;
      return await new Promise<string>((resolve, reject) => {
        // eslint-disable-next-line no-undef
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.warn('persistPickedFile web inline failed', err);
      return uri;
    }
  }
  try {
    const dir = new Directory(Paths.document, 'attachments');
    if (!dir.exists) {
      dir.create({ intermediates: true });
    }
    const safe = fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
    const target = new File(dir, `${Date.now()}_${safe}`);
    const source = new File(uri);
    source.copy(target);
    return target.uri;
  } catch (err) {
    // Fallback: return the original URI. The caller may still be able to
    // display it (cache:// URIs work until the OS cleans them up).
    console.warn('persistPickedFile copy failed', err);
    return uri;
  }
}

export async function deletePersistedFile(uri: string): Promise<void> {
  if (Platform.OS === 'web') return;
  try {
    const file = new File(uri);
    if (file.exists) file.delete();
  } catch (err) {
    console.warn('deletePersistedFile failed', err);
  }
}

/**
 * Read a picked file's bytes as base64 (Wave 51 — feeds PDF OCR).
 * Web: fetch the blob:/data: URL and FileReader it. Native: the new
 * expo-file-system File API reads base64 directly.
 */
export async function readUriAsBase64(uri: string): Promise<string | null> {
  try {
    if (Platform.OS === 'web') {
      // eslint-disable-next-line no-undef
      const res = await fetch(uri);
      const blob = await res.blob();
      return await new Promise<string | null>((resolve) => {
        // eslint-disable-next-line no-undef
        const reader = new FileReader();
        reader.onloadend = () => {
          const s = typeof reader.result === 'string' ? reader.result : null;
          // data:<mime>;base64,XXXX → strip the prefix.
          resolve(s ? s.slice(s.indexOf(',') + 1) : null);
        };
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    }
    const file = new File(uri);
    return file.base64();
  } catch (err) {
    console.warn('readUriAsBase64 failed', err);
    return null;
  }
}

export function isImageMime(mime: string | null | undefined): boolean {
  return !!mime && mime.startsWith('image/');
}

export function isPdfMime(mime: string | null | undefined): boolean {
  return mime === 'application/pdf';
}
